import type { DealStage } from "@/contexts/DealStagesContext";
import { supabase } from "@/lib/supabase";
import type { Tables } from "@/types/database.types";
import { isArchivedProject } from "@/utils/projectUtils";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ProjectSummary } from "./useProjectBoardData";
import { useRealtimeRefresh } from "./useRealtimeRefresh";
import { useUserTimeZone } from "./useUserTimeZone";
import { calendarDateInTimeZone, toISODateString } from "@/utils/dateUtils";

/** A role id (uuid); the employee's primary (first) role. */
type RoleId = string;
type AssignmentEmployee = {
  id: string;
  name: string | null;
  nickname: string | null;
  role: RoleId | null;
  weekly_capacity_hours: number;
};

type RawProject = Tables<"projects"> & {
  deal_stage: DealStage | null;
  office: {
    name: string | null;
    is_active: boolean | null;
  } | null;
  project_assignments: Array<
    Tables<"project_assignments"> & {
      assignment_employees: Array<{
        employee: AssignmentEmployee | null;
      }> | null;
    }
  > | null;
};

function normalizeProjects(data: RawProject[]): ProjectSummary[] {
  return data.map((project) => {
    const { project_assignments, ...projectFields } = project;
    const sortedAssignments = [...(project_assignments ?? [])].sort((a, b) => {
      const orderDiff = (a.display_order ?? 0) - (b.display_order ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return (a.created_at ?? "").localeCompare(b.created_at ?? "");
    });
    return {
      ...projectFields,
      project_assignments:
        sortedAssignments.map((assignment) => {
          const { assignment_employees, ...assignmentFields } = assignment;
          const employees = (assignment_employees ?? [])
            .map((ae) => ae.employee)
            .filter((emp): emp is AssignmentEmployee => emp !== null)
            .map((emp) => {
              const r = emp.role;
              const primaryRole = Array.isArray(r) ? (r[0] ?? null) : (r ?? null);
              return {
                id: emp.id,
                name: emp.name ?? null,
                nickname: emp.nickname ?? null,
                role: primaryRole,
                weekly_capacity_hours: emp.weekly_capacity_hours ?? 40,
              };
            });
          return { ...assignmentFields, employees };
        }),
    };
  });
}

const PROJECT_SELECT = `
  id,
  name,
  color,
  budget,
  category_id,
  deal_stage_id,
  deal_stage:deal_stages ( id, name, likelihood_to_close, kind ),
  likelihood_to_close,
  start_date,
  end_date,
  num_weeks,
  office_id,
  client,
  client_id,
  description,
  potential_future_work,
  tech_stack,
  engagement_type,
  team_success_ingredients,
  created_at,
  updated_at,
  office:offices ( name, is_active ),
  project_assignments (
    id,
    project_id,
    required_role_id,
    weekly_hours_allocated,
    is_confirmed,
    notes,
    display_order,
    created_at,
    updated_at,
    assignment_employees (
      employee:employees ( id, name, nickname, role, weekly_capacity_hours )
    )
  )
`;

export interface UseArchiveDataResult {
  projects: ProjectSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateProjectOptimistically: (projectId: string, updates: Partial<ProjectSummary>) => void;
  refreshSilently: () => Promise<void>;
}

export function useArchiveData(): UseArchiveDataResult {
  const userTimeZone = useUserTimeZone();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isInitialLoad = useRef(true);

  const fetchProjects = useCallback(async () => {
    const scrollPosition = isInitialLoad.current
      ? undefined
      : (window.scrollY || window.pageYOffset);

    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    }

    setLoading(true);

    // Fetch ALL projects (no deal_stage filter) so we can filter client-side
    const { data, error: queryError } = await supabase
      .from("projects")
      .select(PROJECT_SELECT)
      .returns<RawProject[]>()
      .order("start_date", { ascending: true });

    if (queryError) {
      setError(queryError.message ?? "Failed to load archived projects");
      setProjects([]);
      setLoading(false);
      return;
    }

    const normalized = normalizeProjects(data ?? []);
    const today = toISODateString(calendarDateInTimeZone(new Date(), userTimeZone));
    const archived = normalized.filter((project) => isArchivedProject(project, today));

    setProjects(archived);
    setError(null);
    setLoading(false);

    if (scrollPosition !== undefined) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPosition);
        });
      });
    }
  }, [userTimeZone]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const updateProjectOptimistically = useCallback((projectId: string, updates: Partial<ProjectSummary>) => {
    setProjects((prev) =>
      prev.map((project) =>
        project.id === projectId ? { ...project, ...updates } : project
      )
    );
  }, []);

  const refreshSilently = useCallback(async () => {
    const scrollPosition = window.scrollY || window.pageYOffset;

    const { data, error: queryError } = await supabase
      .from("projects")
      .select(PROJECT_SELECT)
      .returns<RawProject[]>()
      .order("start_date", { ascending: true });

    if (queryError) {
      setError(queryError.message ?? "Failed to load archived projects");
      return;
    }

    const normalized = normalizeProjects(data ?? []);
    const today = toISODateString(calendarDateInTimeZone(new Date(), userTimeZone));
    const archived = normalized.filter((project) => isArchivedProject(project, today));

    setProjects(archived);
    setError(null);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    });
  }, [userTimeZone]);

  useRealtimeRefresh(
    ["projects", "project_assignments", "assignment_employees", "employees"],
    refreshSilently
  );

  return {
    projects,
    loading,
    error,
    refresh: fetchProjects,
    updateProjectOptimistically,
    refreshSilently,
  };
}
